import React, { useState, useEffect } from 'react';
import './App.css';
import projectsData from './data/projects.json';
import skillsData from './data/skills.json';

const App = () => {
  const [expandedProject, setExpandedProject] = useState(null);
  const [currentPhrase, setCurrentPhrase] = useState(0);
  const [selectedProject, setSelectedProject] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const phrases = [
    'Computer Science Undergraduate',
    'Full Stack Developer',
    'QA Engineer',
    'Software Testing Specialist',
    'Problem Solver'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPhrase((prev) => (prev + 1) % phrases.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [phrases.length]);

  const projects = projectsData;

  const skills = skillsData;

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleProject = (id) => {
    setExpandedProject(expandedProject === id ? null : id);
  };

  const openPanel = (project) => {
    setSelectedProject(project);
  };

  const closePanel = () => {
    setSelectedProject(null);
  };

  return (
    <div className="app">
      <header className="header">
        <h1>Portfolio</h1>
        <button className="mobile-menu-toggle" onClick={toggleMobileMenu}>
          ☰
        </button>
        <nav className={`nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <ul className="nav-links">
            <li className="nav-link" onClick={() => scrollToSection('about')}>About</li>
            <li className="nav-link" onClick={() => scrollToSection('projects')}>Projects</li>
            <li className="nav-link" onClick={() => scrollToSection('skills')}>Skills</li>
            <li className="nav-link" onClick={() => scrollToSection('education')}>Education</li>
            <li className="nav-link" onClick={() => scrollToSection('contact')}>Contact</li>
          </ul>
        </nav>
      </header>

      <main className="container">
        <section className="hero section">
          <div className="hero-content">
            <h1>
              <span>Hi, I'm</span>
              Manith Samarasinghe
            </h1>
            <div className="rotating-text" key={currentPhrase}>
              {phrases[currentPhrase]}
            </div>
            <p className="hero-description">
              Motivated Computer Science undergraduate with hands-on experience in frontend and backend development, software testing, and automation. 
              Skilled in building responsive user interfaces, developing RESTful APIs, and implementing comprehensive testing frameworks. 
              Actively seeking Software Engineering internship opportunities to contribute to high-impact projects.
            </p>
          </div>
          <div className="hero-photo">
            <div className="profile-photo">
              <img src="/profPicProfessional.jpg" alt="Profile" />
            </div>
          </div>
        </section>

        <section id="about" className="section">
          <h2>Who Am I?</h2>
          <p>
            I am a motivated Computer Science undergraduate with hands-on experience in frontend and backend development, software testing, and automation. I have worked on building responsive user interfaces, developing RESTful APIs, and implementing server-side logic using modern technologies like Java Spring Boot, React, Node.js, and various databases including MySQL and MongoDB.
          </p>
          <br></br>
          <p>
            I am skilled in designing and executing comprehensive test cases, API testing, and end-to-end automation using tools like Selenium, Playwright, and Postman. With experience in cloud platforms like AWS and Azure, along with CI/CD pipelines using GitHub Actions, I am passionate about delivering high-quality software solutions and continuously improving my technical expertise through challenging projects.
          </p>
        </section>

        <section id="education" className="section">
          <h2>Education</h2>
          <div className="education-card">
            <div className="education-header">
              <div className="education-icon">🎓</div>
              <div className="education-info">
                <h3>BSc. (Hons) Computer Science</h3>
                <p className="university">Sri Lanka Institute of Information Technology</p>
                <p className="duration">2023 - 2027 (Expected)</p>
              </div>
            </div>
            <div className="coursework">
              <h4>Relevant Coursework</h4>
              <div className="coursework-list">
                <span className="course-tag">Data Structures</span>
                <span className="course-tag">Algorithms</span>
                <span className="course-tag">Web Development</span>
                <span className="course-tag">Database Systems</span>
                <span className="course-tag">Software Engineering</span>
                <span className="course-tag">OOP</span>
                <span className="course-tag">Operating Systems</span>
                <span className="course-tag">Computer Networks</span>
              </div>
            </div>
          </div>
        </section>

        <section id="projects" className="section">
          <h2>Projects</h2>
          <div className="projects-grid">
            {projects.map(project => (
              <div 
                key={project.id} 
                className="project-item"
                onClick={() => openPanel(project)}
              >
                <div className="project-header">
                  <div>
                    <h3>{project.title}</h3>
                    <p>{project.description}</p>
                  </div>
                  <span className="expand-icon">→</span>
                </div>
              </div>
            ))}
            <a 
              href="https://github.com/ManithMss" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="more-projects-card"
            >

              <h3>Want to see more?</h3>
              <p>Check out all my projects on GitHub</p>
            </a>
          </div>
        </section>

        <div className={`side-panel-overlay ${selectedProject ? 'open' : ''}`} onClick={closePanel}></div>
        <div className={`side-panel ${selectedProject ? 'open' : ''}`}>
          {selectedProject && (
            <>
              <div className="side-panel-header">
                <h3>{selectedProject.title}</h3>
                <button className="close-panel" onClick={closePanel}>×</button>
              </div>
              <div className="project-tech">
                {selectedProject.tech.split(', ').map((tech, index) => (
                  <span key={index} className="tech-badge">{tech}</span>
                ))}
              </div>
              <p className="details">{selectedProject.details}</p>
              <a href={selectedProject.github} target="_blank" rel="noopener noreferrer" className="github-link">
                <span>🔗</span> View on GitHub
              </a>
            </>
          )}
        </div>

        <section id="skills" className="section">
          <h2>Skills</h2>
          <div className="skills-container">
            {Object.entries(skills).map(([category, skillList]) => (
              <div key={category} className="skill-category">
                <h3>{category}</h3>
                {skillList.map((skill, index) => (
                  <div key={index} className="skill-item">
                    <div className="skill-name">
                      <span>{skill.name}</span>
                      <span>{skill.level}%</span>
                    </div>
                    <div className="skill-bar">
                      <div className="skill-progress" style={{ width: `${skill.level}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </section>

        <section id="contact" className="section">
          <h2>Contact</h2>
          <div className="contact-container">
            <p className="contact-intro">
              I'm always open to discussing new opportunities, collaborations, or just having a chat about technology!
            </p>
            <div className="contact-links">
              <a href="mailto:samudithasamarasinghe@gmail.com" className="contact-link">
                <div className="contact-icon">📧</div>
                <div className="contact-info">
                  <div className="contact-label">Email</div>
                  <div className="contact-value">samudithasamarasinghe@gmail.com</div>
                </div>
              </a>
              <a href="tel:+94788773530" className="contact-link">
                <div className="contact-icon">📱</div>
                <div className="contact-info">
                  <div className="contact-label">Phone</div>
                  <div className="contact-value">+94 788773530</div>
                </div>
              </a>
              <div className="contact-link" style={{ cursor: 'default' }}>
                <div className="contact-icon">📍</div>
                <div className="contact-info">
                  <div className="contact-label">Location</div>
                  <div className="contact-value">Malabe, Sri Lanka</div>
                </div>
              </div>
              <a href="https://www.linkedin.com/in/manith-samarasinghe" target="_blank" rel="noopener noreferrer" className="contact-link">
                <div className="contact-icon">👤</div>
                <div className="contact-info">
                  <div className="contact-label">LinkedIn</div>
                  <div className="contact-value">linkedin.com/in/manith-samarasinghe</div>
                </div>
              </a>
              <a href="https://github.com/ManithMss" target="_blank" rel="noopener noreferrer" className="contact-link">
                <div className="contact-icon">👨‍💻</div>
                <div className="contact-info">
                  <div className="contact-label">GitHub</div>
                  <div className="contact-value">github.com/ManithMss</div>
                </div>
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default App;